import React from 'react';

/** What a chart says when it cannot be drawn at all. */
export const ERROR_FALLBACK = "This chart couldn't be drawn. Refresh to try again.";

/**
 * A chart that fails says so in its own frame; the page and the charts beside
 * it carry on (standards §12). This boundary catches whatever throws while a
 * chart renders — a value the renderer did not expect, or a charts chunk that
 * failed to download (an open tab after a deploy replaced the hashed file) —
 * and draws ERROR_FALLBACK in the chart's frame instead.
 *
 * `resetKey` is the result: a new result (a re-run) tries again.
 *
 * It imports nothing but React, so the lazy boundary (LazyCharts.js) can hold
 * it in the main bundle and catch a chunk that never arrives.
 */
export default class ChartBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { failed: false };
	}

	static getDerivedStateFromError() {
		return { failed: true };
	}

	componentDidUpdate(previous) {
		if (this.state.failed && previous.resetKey !== this.props.resetKey) {
			// eslint-disable-next-line react/no-did-update-set-state
			this.setState({ failed: false });
		}
	}

	componentDidCatch(error) {
		if (typeof console !== 'undefined' && console.error) console.error(error);
	}

	render() {
		if (!this.state.failed) return this.props.children;
		const { title, framed = false, headingLevel = 3 } = this.props;
		const Heading = `h${Math.min(6, Math.max(2, Number(headingLevel) || 3))}`;
		const message = (
			<div className='chart-frame__error' role='status'>
				<span>{ERROR_FALLBACK}</span>
			</div>
		);
		if (!framed) return message;
		return (
			<section className='chart-frame' data-form='error'>
				{title ? <Heading className='chart-frame__title'>{title}</Heading> : null}
				<div className='chart-frame__body'>{message}</div>
			</section>
		);
	}
}
